import { getMimeByBase64Signature, normalizeBase64, parseDataUrl, resolveFileName, safeDecodeURIComponent, sanitizeHttpUrl, saveBlob } from '@libs/core';

// Lets the browser decode off the main thread, so multi-MB files don't freeze the UI.
const base64ToBlob = async (base64: string, mime: string): Promise<Blob> => {
    const response = await fetch(`data:${mime};base64,${base64}`);
    if (!response.ok) {
        throw new Error(`base64 decode failed with status ${response.status}`);
    }
    return response.blob();
};

const toFile = async (value: string): Promise<{ blob: Blob; name: string } | null> => {
    const dataUrl = parseDataUrl(value);
    if (dataUrl) {
        const name = resolveFileName(dataUrl.mime, dataUrl.name);
        if (!dataUrl.isBase64) {
            return { blob: new Blob([safeDecodeURIComponent(dataUrl.payload)], { type: dataUrl.mime }), name };
        }
        const base64 = normalizeBase64(dataUrl.payload);
        return base64 ? { blob: await base64ToBlob(base64, dataUrl.mime), name } : null;
    }
    const base64 = normalizeBase64(value);
    if (!base64) {
        return null;
    }
    const mime = getMimeByBase64Signature(base64);
    return { blob: await base64ToBlob(base64, mime), name: resolveFileName(mime) };
};

/**
 * Starts a download for a player `download` action value:
 * - an http(s) link is opened in a new tab (the browser decides whether to show or save it);
 * - a base64 data URL (or bare base64) is saved as a file, its name/extension taken from the value.
 * Invalid values are only logged.
 */
export const downloadFromValue = async (value: unknown): Promise<void> => {
    if (typeof value !== 'string' || !value.trim()) {
        console.error('download: empty value');
        return;
    }
    const url = sanitizeHttpUrl(value.trim());
    if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
    }
    try {
        const file = await toFile(value);
        if (!file) {
            console.error('download: value is neither an http(s) link nor base64');
            return;
        }
        saveBlob(file.blob, file.name);
    } catch (error) {
        console.error('download: failed to save file', error);
    }
};
